import { Link } from "@tanstack/react-router";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";
import { btnPrimary, btnGhostOnDark, container } from "@/lib/ui";

type Variant = "default" | "products" | "about";

/** Always-dark closing call-to-action shared by Home / Products / About. */
export function CtaSection({ variant = "default" }: { variant?: Variant }) {
  const { t } = useTranslation();

  const title =
    variant === "products"
      ? t("cta.products.title")
      : variant === "about"
        ? t("cta.about.title")
        : t("cta.default.title");
  const description =
    variant === "products"
      ? t("cta.products.description")
      : variant === "about"
        ? t("cta.about.description")
        : t("cta.default.description");
  const primary = { to: "/contact", label: t("actions.cooperate") };
  const secondary =
    variant === "about"
      ? { to: "/products", label: t("actions.viewProducts") }
      : { to: "/about", label: t("actions.learnTeam") };

  return (
    <section className="bg-bg py-20 md:py-28">
      <div className={container}>
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#070b10] px-6 py-16 text-white shadow-[0_50px_140px_-50px_rgba(1,120,254,0.5)] sm:rounded-[2.5rem] md:px-12 md:py-24">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/70 to-transparent" />
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-0 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-[radial-gradient(circle,rgba(1,120,254,0.28),transparent_60%)] blur-2xl" />
            <div className="absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(0,200,247,0.2),transparent_60%)] blur-2xl" />
          </div>

          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-white md:text-5xl">
                {title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#9aa4ad] md:text-lg">
                {description}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to={primary.to} className={btnPrimary}>
                  {primary.label}
                  <ArrowOutwardIcon style={{ fontSize: 18 }} />
                </Link>
                <Link to={secondary.to} className={btnGhostOnDark}>
                  {secondary.label}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
